import { useContext, useMemo, useState } from 'react';
import { ArrowDownTrayIcon, DocumentTextIcon, PaperAirplaneIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { maskName } from '../../../utils/privacy';
import { splitSpecies } from '../../../utils/species';
import MaskedName from '../../../components/MaskedName';
import axios from 'axios';
import { AuthContext } from '../../../contexts/Auth/Auth.Context';
import { REACT_APP_UPLOADS } from '../../../constants';

interface Props { allData: any[] }

const MONTHS = [
  'January','February','March','April','May','June',
  'July','August','September','October','November','December',
];

function toCSV(rows: any[]): string {
  if (!rows.length) return '';
  const keys = [
    'species','location','dateOfSeizure','caseStatus','nameOfAccused',
    'nationality','charge1','exhibit','weight','judgmentOutcome','latitude','longitude',
  ];
  const header = keys.join(',');
  const body = rows.map((r) =>
    keys.map((k) => {
      const v = r[k] ?? '';
      return `"${String(v).replace(/"/g, '""')}"`;
    }).join(',')
  ).join('\n');
  return `${header}\n${body}`;
}

function buildFilterSummary(species: string, status: string, month: string, year: string): string {
  const parts: string[] = [];
  if (species !== 'all') parts.push(`Species: ${species}`);
  if (status  !== 'all') parts.push(`Status: ${status}`);
  if (month   !== 'all') parts.push(`Month: ${month}`);
  if (year    !== 'all') parts.push(`Year: ${year}`);
  return parts.length ? parts.join(', ') : 'All records (no filter applied)';
}

type Role = 'guest' | 'user' | 'admin' | 'super-admin';

function resolveRole(token: string | null | undefined, role?: string): Role {
  if (!token) return 'guest';
  const r = role?.toLowerCase();
  if (r === 'admin') return 'admin';
  if (r === 'super-admin') return 'super-admin';
  return 'user';
}

export default function ExportView({ allData }: Props) {
  const { state } = useContext(AuthContext);
  const role = resolveRole(state.userToken, state.userData?.role);
  const canDownload = role === 'admin' || role === 'super-admin';

  const [species,  setSpecies]  = useState('all');
  const [status,   setStatus]   = useState('all');
  const [month,    setMonth]    = useState('all');
  const [year,     setYear]     = useState('all');
  const [exported, setExported] = useState<number | null>(null);

  // Request form fields
  const [reqName,   setReqName]   = useState('');
  const [reqEmail,  setReqEmail]  = useState('');
  const [reqOrg,    setReqOrg]    = useState('');
  const [reqPurpose,setReqPurpose]= useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted,  setSubmitted]  = useState(false);
  const [submitErr,  setSubmitErr]  = useState('');

  const speciesList = useMemo(() => {
    const set = new Set<string>();
    allData.forEach((d: any) => splitSpecies(d.species).forEach((s) => set.add(s.toLowerCase())));
    return Array.from(set).sort();
  }, [allData]);

  const yearList = useMemo(() => {
    const set = new Set<string>();
    allData.forEach((d: any) => {
      if (d.dateOfSeizure) {
        const y = new Date(d.dateOfSeizure).getFullYear();
        if (y > 2000) set.add(String(y));
      }
    });
    return Array.from(set).sort();
  }, [allData]);

  const filtered = useMemo(() => {
    let f = allData;
    if (species !== 'all') f = f.filter((d: any) =>
      splitSpecies(d.species).map((s) => s.toLowerCase()).includes(species)
    );
    if (status !== 'all') f = f.filter((d: any) =>
      d.caseStatus?.toLowerCase() === status.toLowerCase()
    );
    if (month !== 'all') f = f.filter((d: any) =>
      d.dateOfSeizure && new Date(d.dateOfSeizure).toLocaleString('default', { month: 'long' }).toLowerCase() === month.toLowerCase()
    );
    if (year !== 'all') f = f.filter((d: any) =>
      d.dateOfSeizure && new Date(d.dateOfSeizure).getFullYear().toString() === year
    );
    return f;
  }, [allData, species, status, month, year]);

  function downloadCSV() {
    const csv = toCSV(filtered);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `wits-export-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    setExported(filtered.length);
    setTimeout(() => setExported(null), 4000);
  }

  async function submitRequest() {
    if (!reqName.trim() || !reqEmail.trim() || !reqPurpose.trim()) {
      setSubmitErr('Please fill in your name, email and purpose.');
      return;
    }
    setSubmitErr('');
    setSubmitting(true);
    try {
      await axios.post(`${REACT_APP_UPLOADS}/api/v1/data-requests`, {
        requesterName:  reqName.trim(),
        requesterEmail: reqEmail.trim(),
        organisation:   reqOrg.trim(),
        purpose:        reqPurpose.trim(),
        filters:        buildFilterSummary(species, status, month, year),
        recordCount:    filtered.length,
      });
      setSubmitted(true);
    } catch {
      setSubmitErr('Failed to submit request. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }

  const selCls = 'w-full bg-[#0a2e18] border border-white/10 text-white/80 text-xs rounded-lg px-3 py-2.5 focus:outline-none focus:ring-1 focus:ring-green-500/40 appearance-none';
  const inputCls = 'w-full bg-[#0a2e18] border border-white/10 text-white/80 text-xs rounded-lg px-3 py-2.5 focus:outline-none focus:ring-1 focus:ring-green-500/40 placeholder-white/20';

  return (
    <div className="flex-1 overflow-y-auto p-5" style={{ scrollbarWidth: 'thin', scrollbarColor: '#14532d transparent' }}>

      <div className="mb-5">
        <h2 className="text-white/85 text-base font-semibold" style={{ fontFamily: 'var(--font-display)' }}>
          {canDownload ? 'Export Data' : 'Request Data'}
        </h2>
        <p className="text-white/30 text-xs mt-0.5">
          {canDownload
            ? 'Filter records and download the dataset as CSV, or submit a formal data request.'
            : 'Filter the data you need, then submit a request. Our team will review and respond within 2–3 business days.'}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">

        {/* ── Filters ─────────────────────────────────────────── */}
        <div className="md:col-span-2 rounded-xl border border-white/6 p-5" style={{ background: '#0a2e18' }}>
          <p className="text-white/55 text-xs font-semibold tracking-[0.1em] uppercase mb-4">Filter Records</p>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label htmlFor="filter-species" className="text-white/30 text-[10px] uppercase tracking-wider block mb-1.5">Species</label>
              <select id="filter-species" className={selCls} value={species} onChange={(e) => setSpecies(e.target.value)}>
                <option value="all">All Species</option>
                {speciesList.map((s) => <option key={s} value={s} className="capitalize">{s}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="filter-status" className="text-white/30 text-[10px] uppercase tracking-wider block mb-1.5">Case Status</label>
              <select id="filter-status" className={selCls} value={status} onChange={(e) => setStatus(e.target.value)}>
                <option value="all">All Statuses</option>
                <option value="ongoing">Ongoing</option>
                <option value="concluded">Concluded</option>
                <option value="absconded">Absconded</option>
              </select>
            </div>
            <div>
              <label htmlFor="filter-month" className="text-white/30 text-[10px] uppercase tracking-wider block mb-1.5">Month</label>
              <select id="filter-month" className={selCls} value={month} onChange={(e) => setMonth(e.target.value)}>
                <option value="all">All Months</option>
                {MONTHS.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div>
              <label htmlFor="filter-year" className="text-white/30 text-[10px] uppercase tracking-wider block mb-1.5">Year</label>
              <select id="filter-year" className={selCls} value={year} onChange={(e) => setYear(e.target.value)}>
                <option value="all">All Years</option>
                {yearList.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
          </div>
        </div>

        {/* ── Summary + action ───────────────────────────────── */}
        <div className="flex flex-col gap-4">
          <div className="rounded-xl border border-white/6 p-5 flex flex-col gap-3" style={{ background: '#0a2e18' }}>
            <p className="text-white/55 text-xs font-semibold tracking-[0.1em] uppercase">Summary</p>
            {[
              { label: 'Total in dataset', value: allData.length },
              { label: 'Matching filters', value: filtered.length },
              { label: 'Fields included',  value: 12 },
            ].map((s) => (
              <div key={s.label} className="flex items-center justify-between">
                <span className="text-white/35 text-xs">{s.label}</span>
                <span className="text-white/75 text-sm font-bold tabular-nums" style={{ fontFamily: 'var(--font-display)' }}>{s.value}</span>
              </div>
            ))}
          </div>

          {/* Admin / super-admin: direct download */}
          {canDownload && (
            <>
              <button
                onClick={downloadCSV}
                disabled={filtered.length === 0}
                className="flex items-center justify-center gap-2 w-full py-3 rounded-xl bg-green-700 text-white text-sm font-semibold hover:bg-green-600 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
              >
                <ArrowDownTrayIcon className="w-4 h-4" />
                Download CSV
              </button>
              {exported !== null && (
                <div className="rounded-xl border border-emerald-500/20 bg-emerald-500/8 px-4 py-3 text-emerald-400 text-xs flex items-center gap-2">
                  <DocumentTextIcon className="w-4 h-4 shrink-0" />
                  Exported {exported} records successfully.
                </div>
              )}
              <div className="h-px bg-white/6" />
              <p className="text-white/25 text-[10px] text-center">or submit a formal request below</p>
            </>
          )}
        </div>
      </div>

      {/* ── Request form ─────────────────────────────────────────────── */}
      {!submitted ? (
        <div className="mt-5 rounded-xl border border-white/6 p-5" style={{ background: '#0a2e18' }}>
          <p className="text-white/55 text-xs font-semibold tracking-[0.1em] uppercase mb-1">
            {canDownload ? 'Submit a Formal Data Request' : 'Request This Data'}
          </p>
          <p className="text-white/25 text-[10px] mb-4">
            Our team will review your request and respond within 2–3 business days with the filtered dataset.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label htmlFor="req-name" className="text-white/30 text-[10px] uppercase tracking-wider block mb-1.5">Full Name <span className="text-red-400">*</span></label>
              <input id="req-name" className={inputCls} placeholder="Your full name" value={reqName} onChange={(e) => setReqName(e.target.value)} />
            </div>
            <div>
              <label htmlFor="req-email" className="text-white/30 text-[10px] uppercase tracking-wider block mb-1.5">Email Address <span className="text-red-400">*</span></label>
              <input id="req-email" className={inputCls} type="email" placeholder="your@email.com" value={reqEmail} onChange={(e) => setReqEmail(e.target.value)} />
            </div>
            <div>
              <label htmlFor="req-org" className="text-white/30 text-[10px] uppercase tracking-wider block mb-1.5">Organisation</label>
              <input id="req-org" className={inputCls} placeholder="Your organisation (optional)" value={reqOrg} onChange={(e) => setReqOrg(e.target.value)} />
            </div>
            <div>
              <label htmlFor="req-purpose" className="text-white/30 text-[10px] uppercase tracking-wider block mb-1.5">Purpose of Request <span className="text-red-400">*</span></label>
              <input id="req-purpose" className={inputCls} placeholder="Research, policy, journalism…" value={reqPurpose} onChange={(e) => setReqPurpose(e.target.value)} />
            </div>
          </div>

          {/* Filter summary preview */}
          <div className="rounded-lg border border-white/6 px-4 py-3 mb-4" style={{ background: 'rgba(0,0,0,0.15)' }}>
            <p className="text-white/25 text-[9px] uppercase tracking-wider mb-1">Filters that will be included in request</p>
            <p className="text-white/55 text-xs">{buildFilterSummary(species, status, month, year)}</p>
            <p className="text-white/30 text-[10px] mt-1">{filtered.length} matching record{filtered.length !== 1 ? 's' : ''}</p>
          </div>

          {submitErr && (
            <p className="text-red-400 text-xs mb-3">{submitErr}</p>
          )}

          <button
            onClick={submitRequest}
            disabled={submitting || filtered.length === 0}
            className="flex items-center justify-center gap-2 px-6 py-2.5 rounded-xl bg-sky-700/40 text-sky-300 border border-sky-500/25 text-sm font-semibold hover:bg-sky-700/60 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
          >
            {submitting
              ? <><span className="w-4 h-4 rounded-full border-2 border-sky-400 border-t-transparent animate-spin" /> Submitting…</>
              : <><PaperAirplaneIcon className="w-4 h-4" /> Submit Request</>
            }
          </button>
        </div>
      ) : (
        <div className="mt-5 rounded-xl border border-emerald-500/20 p-8 flex flex-col items-center gap-4 text-center" style={{ background: 'rgba(16,185,129,0.06)' }}>
          <CheckCircleIcon className="w-12 h-12 text-emerald-400" />
          <div>
            <p className="text-emerald-400 text-base font-semibold">Request Submitted Successfully</p>
            <p className="text-white/40 text-xs mt-1 max-w-sm">
              Your data request has been sent to our team. A confirmation has been emailed to <strong className="text-white/60">{reqEmail}</strong>. Expect a response within 2–3 business days.
            </p>
          </div>
          <button
            onClick={() => { setSubmitted(false); setReqName(''); setReqEmail(''); setReqOrg(''); setReqPurpose(''); }}
            className="text-white/30 text-xs hover:text-white/55 transition-colors underline underline-offset-2"
          >
            Submit another request
          </button>
        </div>
      )}

      {/* Preview table */}
      {filtered.length > 0 && (
        <div className="mt-5 rounded-xl border border-white/6 overflow-hidden" style={{ background: '#0a2e18' }}>
          <div className="px-5 py-3 border-b border-white/5 flex items-center justify-between">
            <p className="text-white/55 text-xs font-semibold tracking-[0.1em] uppercase">Preview (first 10 rows)</p>
            <span className="text-white/25 text-[10px]">{filtered.length} total rows</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs border-collapse">
              <thead>
                <tr style={{ background: 'rgba(0,0,0,0.15)' }}>
                  {['Species','Location','Date','Status','Accused','Nationality'].map((h) => (
                    <th key={h} className="text-left text-white/25 text-[9px] font-semibold uppercase tracking-wider px-4 py-2.5 whitespace-nowrap">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.slice(0, 10).map((r: any, i: number) => (
                  <tr key={i} className="border-t border-white/4 hover:bg-white/2">
                    <td className="px-4 py-2.5 text-white/70 truncate max-w-[120px]">{r.species || '—'}</td>
                    <td className="px-4 py-2.5 text-white/50 truncate max-w-[120px]">{r.location || '—'}</td>
                    <td className="px-4 py-2.5 text-white/40 whitespace-nowrap">{r.dateOfSeizure || '—'}</td>
                    <td className="px-4 py-2.5 text-white/50 whitespace-nowrap">{r.caseStatus || '—'}</td>
                    <td className="px-4 py-2.5 text-white/50 max-w-[120px]"><MaskedName name={r.nameOfAccused} role={role} /></td>
                    <td className="px-4 py-2.5 text-white/40">{r.nationality || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
